import React, { useState, useCallback, useRef } from 'react';
import { Upload, AlertCircle, Pipette, Copy, Check, Trash2, RotateCcw, X } from 'lucide-react';

type ColorFormat = 'hex' | 'rgb' | 'hsl' | 'cmyk';
type ExportFormat = 'css' | 'json' | 'text';

interface PickedColor {
  hex: string;
  rgb: { r: number; g: number; b: number };
  name: string;
}

const COLOR_NAMES: { name: string; r: number; g: number; b: number }[] = [
  { name: 'Black', r: 0, g: 0, b: 0 },
  { name: 'White', r: 255, g: 255, b: 255 },
  { name: 'Red', r: 255, g: 0, b: 0 },
  { name: 'Crimson', r: 220, g: 20, b: 60 },
  { name: 'Tomato', r: 255, g: 99, b: 71 },
  { name: 'Coral', r: 255, g: 127, b: 80 },
  { name: 'Orange', r: 255, g: 165, b: 0 },
  { name: 'Gold', r: 255, g: 215, b: 0 },
  { name: 'Yellow', r: 255, g: 255, b: 0 },
  { name: 'Khaki', r: 240, g: 230, b: 140 },
  { name: 'Lime', r: 0, g: 255, b: 0 },
  { name: 'Forest Green', r: 34, g: 139, b: 34 },
  { name: 'Green', r: 0, g: 128, b: 0 },
  { name: 'Teal', r: 0, g: 128, b: 128 },
  { name: 'Cyan', r: 0, g: 255, b: 255 },
  { name: 'Sky Blue', r: 135, g: 206, b: 235 },
  { name: 'Steel Blue', r: 70, g: 130, b: 180 },
  { name: 'Royal Blue', r: 65, g: 105, b: 225 },
  { name: 'Blue', r: 0, g: 0, b: 255 },
  { name: 'Navy', r: 0, g: 0, b: 128 },
  { name: 'Indigo', r: 75, g: 0, b: 130 },
  { name: 'Purple', r: 128, g: 0, b: 128 },
  { name: 'Violet', r: 238, g: 130, b: 238 },
  { name: 'Magenta', r: 255, g: 0, b: 255 },
  { name: 'Pink', r: 255, g: 192, b: 203 },
  { name: 'Hot Pink', r: 255, g: 105, b: 180 },
  { name: 'Brown', r: 139, g: 69, b: 19 },
  { name: 'Chocolate', r: 210, g: 105, b: 30 },
  { name: 'Tan', r: 210, g: 180, b: 140 },
  { name: 'Silver', r: 192, g: 192, b: 192 },
  { name: 'Gray', r: 128, g: 128, b: 128 },
  { name: 'Dark Gray', r: 64, g: 64, b: 64 },
  { name: 'Slate Gray', r: 112, g: 128, b: 144 },
];

const getColorName = (r: number, g: number, b: number): string => {
  let best = COLOR_NAMES[0].name;
  let bestDist = Infinity;
  for (const c of COLOR_NAMES) {
    const dist = (c.r - r) ** 2 + (c.g - g) ** 2 + (c.b - b) ** 2;
    if (dist < bestDist) {
      bestDist = dist;
      best = c.name;
    }
  }
  return best;
};

const rgbToHex = (r: number, g: number, b: number) =>
  '#' + [r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('');

const rgbToHsl = (r: number, g: number, b: number) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0, s = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
    else if (max === g) h = ((b - r) / d + 2) / 6;
    else h = ((r - g) / d + 4) / 6;
  }
  return `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
};

const rgbToCmyk = (r: number, g: number, b: number) => {
  if (r === 0 && g === 0 && b === 0) return 'cmyk(0%, 0%, 0%, 100%)';
  const c = 1 - r / 255;
  const m = 1 - g / 255;
  const y = 1 - b / 255;
  const k = Math.min(c, m, y);
  return `cmyk(${Math.round(((c - k) / (1 - k)) * 100)}%, ${Math.round(((m - k) / (1 - k)) * 100)}%, ${Math.round(((y - k) / (1 - k)) * 100)}%, ${Math.round(k * 100)}%)`;
};

const getContrastRatio = (c1: PickedColor, c2: PickedColor): number => {
  const luminance = (r: number, g: number, b: number) => {
    const [rs, gs, bs] = [r, g, b].map((v) => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
    });
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
  };
  const l1 = luminance(c1.rgb.r, c1.rgb.g, c1.rgb.b);
  const l2 = luminance(c2.rgb.r, c2.rgb.g, c2.rgb.b);
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  return (lighter + 0.05) / (darker + 0.05);
};

const ColorPickerFromImage: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [imgElement, setImgElement] = useState<HTMLImageElement | null>(null);
  const [hoveredColor, setHoveredColor] = useState<PickedColor | null>(null);
  const [palette, setPalette] = useState<PickedColor[]>([]);
  const [selectedIndices, setSelectedIndices] = useState<number[]>([]);
  const [copied, setCopied] = useState<string | null>(null);
  const [activeFormat, setActiveFormat] = useState<ColorFormat>('hex');
  const [magnifierPos, setMagnifierPos] = useState<{ x: number; y: number } | null>(null);
  const [magnifierPixels, setMagnifierPixels] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }
    setFile(f);
    setError(null);
    setPalette([]);
    setSelectedIndices([]);
    setHoveredColor(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      const img = new window.Image();
      img.onload = () => {
        setImgElement(img);
        const canvas = canvasRef.current;
        if (!canvas) return;
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(img, 0, 0);
      };
      img.onerror = () => setError('Failed to load image.');
      img.src = url;
    };
    reader.readAsDataURL(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  const getPixelInfo = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const x = Math.floor((e.clientX - rect.left) * scaleX);
    const y = Math.floor((e.clientY - rect.top) * scaleY);
    return { x, y, ctx, scaleX, scaleY, rect };
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const info = getPixelInfo(e);
    if (!info) return;
    const { x, y, ctx } = info;
    const pixel = ctx.getImageData(x, y, 1, 1).data;
    const [r, g, b] = pixel;

    setHoveredColor({
      hex: rgbToHex(r, g, b),
      rgb: { r, g, b },
      name: getColorName(r, g, b),
    });

    // Magnifier: get surrounding 15x15 pixels
    const magSize = 15;
    const half = Math.floor(magSize / 2);
    const pixels: string[] = [];
    const canvas = canvasRef.current!;
    for (let dy = -half; dy <= half; dy++) {
      for (let dx = -half; dx <= half; dx++) {
        const px = x + dx;
        const py = y + dy;
        if (px >= 0 && px < canvas.width && py >= 0 && py < canvas.height) {
          const p = ctx.getImageData(px, py, 1, 1).data;
          pixels.push(rgbToHex(p[0], p[1], p[2]));
        } else {
          pixels.push('#000000');
        }
      }
    }
    setMagnifierPixels(pixels);
    setMagnifierPos({ x: e.clientX - info.rect.left, y: e.clientY - info.rect.top });
  }, [getPixelInfo]);

  const handleMouseLeave = useCallback(() => {
    setMagnifierPos(null);
  }, []);

  const handleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    const info = getPixelInfo(e);
    if (!info) return;
    const { x, y, ctx } = info;
    const pixel = ctx.getImageData(x, y, 1, 1).data;
    const [r, g, b] = pixel;
    const hex = rgbToHex(r, g, b);

    if (palette.length >= 20) return;
    if (palette.some((c) => c.hex === hex)) return;

    setPalette((prev) => [...prev, {
      hex,
      rgb: { r, g, b },
      name: getColorName(r, g, b),
    }]);
  }, [getPixelInfo, palette]);

  const removeColor = (index: number) => {
    setPalette((prev) => prev.filter((_, i) => i !== index));
    setSelectedIndices((prev) => prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i)));
  };

  const toggleSelect = (index: number) => {
    setSelectedIndices((prev) => {
      if (prev.includes(index)) return prev.filter((i) => i !== index);
      if (prev.length >= 2) return [prev[1], index];
      return [...prev, index];
    });
  };

  const formatColor = (color: PickedColor, format: ColorFormat): string => {
    const { r, g, b } = color.rgb;
    switch (format) {
      case 'hex': return color.hex;
      case 'rgb': return `rgb(${r}, ${g}, ${b})`;
      case 'hsl': return rgbToHsl(r, g, b);
      case 'cmyk': return rgbToCmyk(r, g, b);
    }
  };

  const copyValue = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(value);
      setTimeout(() => setCopied(null), 1500);
    } catch (err) {
      console.error('Copy failed:', err);
    }
  };

  const exportPalette = (format: ExportFormat) => {
    let text = '';
    switch (format) {
      case 'css':
        text = ':root {\n' + palette.map((c, i) => `  --color-${i + 1}: ${c.hex};`).join('\n') + '\n}';
        break;
      case 'json':
        text = JSON.stringify(palette.map((c) => ({
          hex: c.hex,
          rgb: `rgb(${c.rgb.r}, ${c.rgb.g}, ${c.rgb.b})`,
          name: c.name,
        })), null, 2);
        break;
      case 'text':
        text = palette.map((c) => `${c.hex} - ${c.name}`).join('\n');
        break;
    }
    copyValue(text);
  };

  const sortByHue = () => {
    setPalette((prev) => [...prev].sort((a, b) => {
      const hslA = rgbToHsl(a.rgb.r, a.rgb.g, a.rgb.b);
      const hslB = rgbToHsl(b.rgb.r, b.rgb.g, b.rgb.b);
      const hueA = parseInt(hslA.match(/\d+/)![0]);
      const hueB = parseInt(hslB.match(/\d+/)![0]);
      return hueA - hueB;
    }));
    setSelectedIndices([]);
  };

  const reset = () => {
    setFile(null);
    setImgElement(null);
    setHoveredColor(null);
    setPalette([]);
    setSelectedIndices([]);
    setError(null);
    setMagnifierPos(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const contrastPair = selectedIndices.length === 2 ? {
    c1: palette[selectedIndices[0]],
    c2: palette[selectedIndices[1]],
    ratio: getContrastRatio(palette[selectedIndices[0]], palette[selectedIndices[1]]),
  } : null;

  const FORMATS: ColorFormat[] = ['hex', 'rgb', 'hsl', 'cmyk'];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-rose-100 rounded-full">
            <Pipette className="w-8 h-8 text-rose-600" />
          </div>
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Color Picker from Image</h2>
        <p className="text-gray-600">
          Pick colors with a magnifier, build palettes, check contrast ratios.
        </p>
      </div>

      {/* Upload */}
      {!file && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-rose-400 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Drop an image here or click to select
          </h3>
          <p className="text-gray-600">Supports PNG, JPG, WebP and more</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-500 mr-2 mt-0.5 flex-shrink-0" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {file && imgElement && (
        <div className="space-y-4">
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Canvas with magnifier */}
            <div className="lg:col-span-2">
              <h3 className="text-sm font-medium text-gray-700 mb-2">
                Click to pick colors (max 20)
              </h3>
              <div className="relative bg-gray-100 rounded-xl overflow-hidden flex items-center justify-center">
                <canvas
                  ref={canvasRef}
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  onClick={handleClick}
                  className="max-w-full max-h-[500px] object-contain cursor-crosshair"
                />
                {/* Magnifier */}
                {magnifierPos && magnifierPixels.length > 0 && (
                  <div
                    className="absolute pointer-events-none border-2 border-white rounded-full shadow-lg overflow-hidden"
                    style={{
                      width: 120,
                      height: 120,
                      left: magnifierPos.x - 60,
                      top: magnifierPos.y - 130,
                    }}
                  >
                    <div
                      className="grid w-full h-full"
                      style={{
                        gridTemplateColumns: 'repeat(15, 1fr)',
                        gridTemplateRows: 'repeat(15, 1fr)',
                      }}
                    >
                      {magnifierPixels.map((color, i) => (
                        <div
                          key={i}
                          style={{ backgroundColor: color }}
                          className={i === Math.floor(magnifierPixels.length / 2) ? 'ring-1 ring-white' : ''}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Color Info + Palette */}
            <div className="space-y-4">
              {/* Current Color */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="text-sm font-medium text-gray-700 mb-3">Current Color</h3>
                {hoveredColor ? (
                  <div className="space-y-3">
                    <div
                      className="w-full h-16 rounded-lg border border-gray-200"
                      style={{ backgroundColor: hoveredColor.hex }}
                    />
                    <p className="text-sm font-medium text-gray-800">{hoveredColor.name}</p>

                    {/* Format Tabs */}
                    <div className="flex gap-1">
                      {FORMATS.map((fmt) => (
                        <button
                          key={fmt}
                          onClick={() => setActiveFormat(fmt)}
                          className={`flex-1 px-1 py-1 rounded text-xs font-medium uppercase transition-colors ${
                            activeFormat === fmt
                              ? 'bg-rose-600 text-white'
                              : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-100'
                          }`}
                        >
                          {fmt}
                        </button>
                      ))}
                    </div>

                    {/* All format values */}
                    {FORMATS.map((fmt) => {
                      const val = formatColor(hoveredColor, fmt);
                      return (
                        <div key={fmt} className={`flex items-center justify-between ${fmt !== activeFormat ? 'opacity-50' : ''}`}>
                          <div className="min-w-0 flex-1">
                            <span className="text-xs text-gray-500 uppercase">{fmt}</span>
                            <p className="text-sm font-mono truncate">{val}</p>
                          </div>
                          <button
                            onClick={() => copyValue(val)}
                            className="p-1 text-gray-400 hover:text-gray-600 flex-shrink-0 ml-2"
                          >
                            {copied === val ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">Hover over the image</p>
                )}
              </div>

              {/* Palette */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-medium text-gray-700">Palette ({palette.length}/20)</h3>
                  <div className="flex gap-1">
                    {palette.length > 1 && (
                      <button
                        onClick={sortByHue}
                        className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1 rounded bg-white border border-gray-200"
                      >
                        Sort
                      </button>
                    )}
                    {palette.length > 0 && (
                      <button onClick={() => { setPalette([]); setSelectedIndices([]); }} className="text-gray-400 hover:text-gray-600 p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
                {palette.length > 0 ? (
                  <div className="space-y-1.5 max-h-[250px] overflow-y-auto">
                    {palette.map((c, i) => (
                      <div
                        key={i}
                        onClick={() => toggleSelect(i)}
                        className={`flex items-center gap-2 p-1.5 rounded-lg cursor-pointer transition-colors ${
                          selectedIndices.includes(i)
                            ? 'bg-rose-50 ring-2 ring-rose-300'
                            : 'hover:bg-gray-100'
                        }`}
                      >
                        <div
                          className="w-7 h-7 rounded border border-gray-200 flex-shrink-0"
                          style={{ backgroundColor: c.hex }}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-mono truncate">{c.hex}</p>
                          <p className="text-[10px] text-gray-500 truncate">{c.name}</p>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); copyValue(c.hex); }}
                          className="p-0.5 text-gray-400 hover:text-gray-600"
                        >
                          {copied === c.hex ? <Check className="w-3 h-3 text-green-500" /> : <Copy className="w-3 h-3" />}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); removeColor(i); }}
                          className="p-0.5 text-gray-400 hover:text-red-500"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm">Click on the image to add colors</p>
                )}

                {/* Contrast checker */}
                {contrastPair && (
                  <div className="mt-3 p-3 bg-white rounded-lg border border-gray-200">
                    <h4 className="text-xs font-medium text-gray-700 mb-2">WCAG Contrast Ratio</h4>
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded" style={{ backgroundColor: contrastPair.c1.hex }} />
                      <span className="text-xs text-gray-500">vs</span>
                      <div className="w-6 h-6 rounded" style={{ backgroundColor: contrastPair.c2.hex }} />
                      <span className="text-lg font-bold ml-auto">{contrastPair.ratio.toFixed(2)}:1</span>
                    </div>
                    <div className="space-y-1 text-xs">
                      <div className="flex justify-between">
                        <span>AA Normal (4.5:1)</span>
                        <span className={contrastPair.ratio >= 4.5 ? 'text-green-600 font-bold' : 'text-red-500'}>
                          {contrastPair.ratio >= 4.5 ? 'Pass' : 'Fail'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>AA Large (3:1)</span>
                        <span className={contrastPair.ratio >= 3 ? 'text-green-600 font-bold' : 'text-red-500'}>
                          {contrastPair.ratio >= 3 ? 'Pass' : 'Fail'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>AAA Normal (7:1)</span>
                        <span className={contrastPair.ratio >= 7 ? 'text-green-600 font-bold' : 'text-red-500'}>
                          {contrastPair.ratio >= 7 ? 'Pass' : 'Fail'}
                        </span>
                      </div>
                    </div>
                    <div
                      className="mt-2 p-2 rounded text-center text-sm font-medium"
                      style={{ backgroundColor: contrastPair.c2.hex, color: contrastPair.c1.hex }}
                    >
                      Sample Text
                    </div>
                  </div>
                )}

                {/* Export */}
                {palette.length > 0 && (
                  <div className="mt-3">
                    <h4 className="text-xs font-medium text-gray-700 mb-2">Export Palette</h4>
                    <div className="flex gap-1">
                      {(['css', 'json', 'text'] as ExportFormat[]).map((fmt) => (
                        <button
                          key={fmt}
                          onClick={() => exportPalette(fmt)}
                          className="flex-1 px-2 py-1.5 text-xs font-medium bg-white text-gray-600 border border-gray-200 rounded hover:bg-rose-50 hover:text-rose-600 transition-colors uppercase"
                        >
                          {fmt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Reset */}
          <div className="flex justify-end">
            <button
              onClick={reset}
              className="flex items-center justify-center gap-2 px-4 py-3 bg-gray-200 text-gray-700 font-medium rounded-xl hover:bg-gray-300 transition-colors"
            >
              <RotateCcw className="w-5 h-5" />
              Start Over
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ColorPickerFromImage;
